import { useMemo, useState } from "react";
import { useMapImages } from "@core/api/queries";
import type { MapImageView } from "@core/api/types";
import { MapEditor } from "@core/components/MapEditor";
import { MapView, type MapPolygon } from "@core/components/MapView";
import { Sheet } from "@core/components/Sheet";
import { useCurrentUser } from "@core/pages/AuthPages";
import { useSm, type CultureEntry, type MapAreaEntry, type MapEntry, type RegionEntry } from "./view";

/** What is shown on a map: the areas to highlight, faint context outlines, and the entry whose own areas the admin edits. */
interface MapSubject {
  kind: "culture" | "region";
  id: string;
  name: string;
  /** The entry's own areas, as they stand in its data file. */
  own: MapAreaEntry[];
  highlight: MapAreaEntry[];
  context: MapAreaEntry[];
}

interface ShownMap {
  map: MapEntry;
  image: MapImageView;
  areas: MapPolygon[];
  context: MapPolygon[];
  note?: string | null;
}

const polygonsOn = (areas: MapAreaEntry[], map: string): MapPolygon[] => areas.filter((a) => a.map === map).flatMap((a) => a.polygons);

/** The sub-regions of a top-level region, in data order. */
export function subRegions(regions: RegionEntry[], id: string): RegionEntry[] {
  return regions.filter((r) => r.parent === id);
}

function cultureSubject(culture: CultureEntry, regions: RegionEntry[]): MapSubject {
  const own = new Set(culture.regions);
  return {
    kind: "culture",
    id: culture.id,
    name: culture.name,
    own: culture.mapAreas,
    highlight: culture.mapAreas,
    context: regions.filter((r) => r.parent && own.has(r.id)).flatMap((r) => r.mapAreas),
  };
}

function regionSubject(region: RegionEntry, regions: RegionEntry[]): MapSubject {
  return {
    kind: "region",
    id: region.id,
    name: region.name,
    own: region.mapAreas,
    highlight: [...region.mapAreas, ...subRegions(regions, region.id).flatMap((r) => r.mapAreas)],
    context: [],
  };
}

/**
 * The map a subject is shown on: the first uploaded map with a highlighted area, else one with context outlines;
 * for admins any uploaded map, so they can draw an outline.
 */
function useSubjectMap(subject: MapSubject | undefined): ShownMap | undefined {
  const { view, character } = useSm();
  const images = useMapImages(character.system);
  const admin = useCurrentUser().role === "admin";
  if (!subject) return undefined;
  const uploaded = (view.catalog?.maps ?? []).flatMap((map): ShownMap[] => {
    const image = images.data?.find((i) => i.id === map.id && i.uploaded);
    if (!image) return [];
    const note = subject.highlight.find((a) => a.map === map.id && a.note)?.note;
    return [{ map, image, areas: polygonsOn(subject.highlight, map.id), context: polygonsOn(subject.context, map.id), note }];
  });
  return uploaded.find((u) => u.areas.length > 0) ?? uploaded.find((u) => u.context.length > 0) ?? (admin ? uploaded[0] : undefined);
}

function useCultureSubject(culture: CultureEntry | undefined): MapSubject | undefined {
  const { view } = useSm();
  return culture ? cultureSubject(culture, view.catalog?.regions ?? []) : undefined;
}

function useRegionSubject(region: RegionEntry): MapSubject {
  const { view } = useSm();
  return regionSubject(region, view.catalog?.regions ?? []);
}

function SubjectMap({ subject, shown, label }: { subject: MapSubject; shown: ShownMap | undefined; label: string }) {
  const { character } = useSm();
  if (!shown || (shown.areas.length === 0 && shown.context.length === 0)) return null;
  return (
    <div className="stack culture-map">
      <MapView system={character.system} map={shown.map.id} areas={shown.areas} context={shown.context} label={`${label} auf der Karte ${shown.map.name}`} />
      {shown.note && <p className="muted small">{shown.note}</p>}
      {subject.kind === "culture" && shown.context.length > 0 && <p className="muted small">Gestrichelt: die Regionen der Kultur.</p>}
    </div>
  );
}

/** Opens the culture on the map; nothing without an uploaded map, or for non-admins without an area. */
export function CultureMapButton({ culture, onShow }: { culture: CultureEntry; onShow: () => void }) {
  const shown = useSubjectMap(useCultureSubject(culture));
  if (!shown) return null;
  return (
    <button type="button" className="button secondary" onClick={onShow} aria-label={`${culture.name} auf der Karte`}>
      Karte
    </button>
  );
}

/** The home of a culture on the map, with the outlines of its regions; nothing without an uploaded map or an area. */
export function CultureMap({ culture }: { culture: CultureEntry }) {
  const subject = useCultureSubject(culture)!;
  const shown = useSubjectMap(subject);
  return <SubjectMap subject={subject} shown={shown} label={culture.name} />;
}

export function CultureMapSheet({ culture, onClose }: { culture: CultureEntry; onClose: () => void }) {
  const subject = useCultureSubject(culture)!;
  return <SubjectSheet subject={subject} title={culture.name} label={culture.name} onClose={onClose} />;
}

/** Opens a region on the map, a top-level region together with its sub-regions; nothing without an uploaded map, or for non-admins without an area. */
export function RegionMapButton({ region, onShow }: { region: RegionEntry; onShow: () => void }) {
  const shown = useSubjectMap(useRegionSubject(region));
  if (!shown) return null;
  return (
    <button type="button" className="button secondary small" onClick={onShow} aria-label={`Region ${region.name} auf der Karte`}>
      Karte
    </button>
  );
}

export function RegionMapSheet({ region, onClose }: { region: RegionEntry; onClose: () => void }) {
  const subject = useRegionSubject(region);
  return <SubjectSheet subject={subject} title={`Region ${region.name}`} label={`Region ${region.name}`} onClose={onClose} />;
}

function SubjectSheet({ subject, title, label, onClose }: { subject: MapSubject; title: string; label: string; onClose: () => void }) {
  const shown = useSubjectMap(subject);
  const admin = useCurrentUser().role === "admin";
  const [editing, setEditing] = useState(false);
  const what = subject.kind === "culture" ? "diese Kultur" : "diese Region";
  return (
    <Sheet title={title} onClose={onClose} wide>
      <div className="stack">
        {shown && editing ? (
          <OutlineEditor subject={subject} map={shown.map} image={shown.image} />
        ) : (
          <>
            <SubjectMap subject={subject} shown={shown} label={label} />
            {shown && shown.areas.length === 0 && <p className="muted">Für {what} ist auf der Karte noch kein Gebiet erfasst.</p>}
          </>
        )}
        {admin && shown && (
          <div className="actions">
            <button type="button" className="button secondary" onClick={() => setEditing(!editing)}>
              {editing ? "Bearbeitung beenden" : "Umriss bearbeiten"}
            </button>
          </div>
        )}
      </div>
    </Sheet>
  );
}

/** The `mapAreas` property as it stands in cultures.json or regions.json, ready to paste. */
export function formatMapAreas(areas: MapAreaEntry[]): string {
  const point = ([x, y]: number[]) => `[${x}, ${y}]`;
  const area = (a: MapAreaEntry) =>
    [
      "  {",
      `    "map": ${JSON.stringify(a.map)},`,
      `    "polygons": [`,
      a.polygons.map((p) => `      [${p.map(point).join(", ")}]`).join(",\n"),
      a.note ? "    ]," : "    ]",
      ...(a.note ? [`    "note": ${JSON.stringify(a.note)}`] : []),
      "  }",
    ].join("\n");
  return `"mapAreas": [\n${areas.map(area).join(",\n")}\n]`;
}

/**
 * Admin editor for the own outline of a culture or region (a top-level region's sub-regions keep theirs).
 * Saving into a package comes later; for now the result is copied into the data file.
 */
function OutlineEditor(props: { subject: MapSubject; map: MapEntry; image: MapImageView }) {
  const { character } = useSm();
  const { subject, map } = props;
  const [polygons, setPolygons] = useState<number[][][]>(() => subject.own.find((a) => a.map === map.id)?.polygons.map((p) => p.map((pt) => [...pt])) ?? []);
  const [copied, setCopied] = useState(false);
  const areas = useMemo(() => {
    const own = subject.own.find((a) => a.map === map.id);
    const edited: MapAreaEntry = { map: map.id, polygons: polygons.filter((p) => p.length > 0), note: own?.note };
    const rest = subject.own.filter((a) => a.map !== map.id);
    return edited.polygons.length > 0 ? [...subject.own.map((a) => (a.map === map.id ? edited : a)), ...(own ? [] : [edited])] : rest;
  }, [subject.own, map.id, polygons]);
  const json = formatMapAreas(areas);
  const file = subject.kind === "culture" ? "cultures.json" : "regions.json";
  const kind = subject.kind === "culture" ? "Kultur" : "Region";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([json + "\n"], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${subject.id}.mapAreas.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="stack">
      <MapEditor
        system={character.system}
        image={props.image}
        value={polygons}
        onChange={(value) => {
          setPolygons(value);
          setCopied(false);
        }}
      />
      <label className="field">
        <span>
          Umrisse für <code>{file}</code> ({kind} <code>{subject.id}</code>, Karte {map.name}). Die App speichert sie noch nicht; ersetze damit den Eintrag
          <code> mapAreas</code> der {kind}.
        </span>
        <textarea className="map-json" readOnly rows={8} value={json} aria-label="mapAreas als JSON" onFocus={(e) => e.target.select()} />
      </label>
      <div className="actions">
        <button type="button" className="button secondary" onClick={() => void copy()}>
          {copied ? "Kopiert" : "JSON kopieren"}
        </button>
        <button type="button" className="button secondary" onClick={download}>
          Herunterladen
        </button>
      </div>
    </div>
  );
}
